// @ts-nocheck

import React, { useMemo } from 'react';
import styled, { css } from 'styled-components';

import { Target } from '../../client';
import LineUpOpenViewButtonContainer from '../../modules/line-up/components/LineUpOpenViewButton/LineUpOpenViewButtonContainer';
import { AutoScrollAnchor, BetGroupLabel, BetGroupWrapper } from '../../styled-components/shared';
import type { BetTemplate } from '../../types/bet';
import { type BetGroupOutcome, PresentationModes, type PresentationItem } from '../../types/betGroup';
import { getNbColumns } from '../../utils/betTemplate';
import BetGroupOutcomeContainer from '../BetGroupOutcome/BetGroupOutcomeContainer';
import SliderLine from '../SliderLine';

const Wrap = styled.div`
  margin: 0.6em 0;
  padding: 0 0.6em;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: ${Target.__MOBILE__ ? '100%' : undefined};
`;

const RowWrapper = styled.div`
  position: relative;

  width: 100%;

  &:not(:last-child) {
    margin-bottom: 0.4em;
  }
`;

const Row = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: ${({ $isTwoCol }) => ($isTwoCol ? 'center' : 'flex-start')};
  align-items: center;
  gap: 0.8em;
`;

const StyledBetGroupOutcomeContainer = styled(BetGroupOutcomeContainer)`
  position: relative;
  width: 100%;

  ${({ $isTwoCol }) =>
    $isTwoCol
      ? css`
          max-width: 50%;
        `
      : css`
          max-width: calc(33.333% - 0.5em);
        `}
`;

const StyledLineUpOpenViewButtonContainer = styled(LineUpOpenViewButtonContainer)`
  margin-right: 0.5em;

  &:only-child {
    margin-left: auto;
  }
`;

type BetGroupLinesProps = {
  className?: string;
  groupLabel: string | null;
  items: Array<BetGroupOutcome>;
  showPercentDistribution: boolean;
  betTemplate: BetTemplate;
  teamId?: number;
  matchId: number;
  displayLabel?: boolean;
  betPresentationMode: PresentationItem;
  showLineUpButton?: boolean;
};

const BetGroupLines = ({
  className,
  groupLabel,
  items,
  showPercentDistribution,
  betTemplate,
  teamId,
  matchId,
  displayLabel,
  betPresentationMode,
  showLineUpButton = false,
}: BetGroupLinesProps) => {
  const isTwoCol = useMemo(() => getNbColumns(betTemplate, items?.length ?? 0) % 2 === 0, [betTemplate, items?.length]);

  const processedItems = useMemo(() => {
    const res = [];

    for (let i = 0; i < items?.length; i++) {
      const item = items[i];
      if (i % (isTwoCol ? 2 : 3) === 0) {
        res.push([]);
      }
      res[res.length - 1].push(item);
    }

    return res;
  }, [items, isTwoCol]);
  const defaultMarketId = useMemo(() => items[0].marketId, [items]);
  const defaultSBV = useMemo(() => items[0].specialBetValue, [items]);

  return (
    <Wrap className={className}>
      {groupLabel || teamId ? (
        <BetGroupWrapper>
          {groupLabel ? <BetGroupLabel>{groupLabel}</BetGroupLabel> : null}
          {showLineUpButton ? (
            <StyledLineUpOpenViewButtonContainer
              teamId={teamId}
              marketId={defaultMarketId}
              specialBetValue={defaultSBV}
              matchId={matchId}
            />
          ) : null}
        </BetGroupWrapper>
      ) : null}
      {betPresentationMode?.type === PresentationModes.SLIDER_BY_OUTCOME ? (
        <RowWrapper>
          <SliderLine
            outcomes={items}
            betTemplate={betTemplate}
            betPresentationMode={betPresentationMode}
            BetGroupComponent={StyledBetGroupOutcomeContainer}
            hideTooltip
          />
        </RowWrapper>
      ) : (
        processedItems.map((row, i) => {
          const { matchId, betId } = row.find((k) => k?.betId && k?.matchId) ?? {};
          const anchorId = betId && matchId ? `match-group-${matchId}-${betId}` : null;

          return (
            <RowWrapper key={`line-n${i}`}>
              {anchorId ? <AutoScrollAnchor data-anchorid={anchorId} /> : null}

              <Row $isTwoCol={isTwoCol}>
                {row.map(({ matchId, betId, id }, j) => (
                  <StyledBetGroupOutcomeContainer
                    key={`betgroup-${matchId ?? ''}-${betId ?? ''}-${id ?? ''}-${i + j}`}
                    $isTwoCol={isTwoCol}
                    matchId={matchId}
                    betId={betId}
                    oddId={id}
                    betTemplate={betTemplate}
                    showPercentDistribution={showPercentDistribution}
                    displayLabel={displayLabel}
                  />
                ))}
              </Row>
            </RowWrapper>
          );
        })
      )}
    </Wrap>
  );
};

export default BetGroupLines;
